import { useState } from 'react';

import {
  Alert,
  Badge,
  Box,
  Button,
  Code,
  Group,
  Loader,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertTriangle, IconRefresh } from '@tabler/icons-react';

import { useReportFailures } from '@/hooks/useReportFailures';
import { ReportRunFailure } from '@/types/report';

/** LangGraph's verdict, which the exception text alone can't tell you. */
const RUN_STATUS_TONE: Record<string, string> = {
  error: 'red',
  timeout: 'orange',
  interrupted: 'yellow',
};

function attemptLabel(failure: ReportRunFailure): string {
  return failure.attempt_no === 0 ? 'Original failure' : `Retry ${failure.attempt_no}`;
}

/** How many times this exact error has come back, across attempts. */
function repeatCount(failures: ReportRunFailure[], failure: ReportRunFailure): number {
  return failures.filter(
    (f) =>
      f.subject_id === failure.subject_id &&
      f.error_message != null &&
      f.error_message === failure.error_message
  ).length;
}

/**
 * Admin-only view of what actually broke, and the action to re-fire it.
 *
 * Shows the attempt history rather than guessing whether a failure is
 * transient: a repeated identical error is the honest signal that retrying
 * won't help, and it needs no classifier to say so.
 */
export function ReportFailurePanel({ reportId }: Readonly<{ reportId: string }>) {
  const { failures, isLoading, isRetrying, error, retry } = useReportFailures(reportId);
  const [retryingSubject, setRetryingSubject] = useState<string | null>(null);

  const handleRetry = async (failure: ReportRunFailure) => {
    setRetryingSubject(failure.subject_id);
    try {
      await retry(failure.subject_id);
      notifications.show({
        color: 'blue',
        title: 'Retry started',
        message: failure.subject_name
          ? `Re-running the failed work for ${failure.subject_name}.`
          : 'Re-running the failed work.',
      });
    } catch (err) {
      notifications.show({
        color: 'red',
        title: 'Could not retry',
        message: err instanceof Error ? err.message : 'Please try again.',
      });
    } finally {
      setRetryingSubject(null);
    }
  };

  if (isLoading && failures.length === 0) {
    return <Loader size="sm" />;
  }
  if (failures.length === 0) {
    // Failures before this shipped were never recorded — say so rather
    // than implying the report failed for no reason.
    return (
      <Text size="sm" c="dimmed">
        No failure detail was recorded for this report.
      </Text>
    );
  }

  // Newest first from the API; the newest per subject is the live one.
  const latestBySubject = new Map<string | null, ReportRunFailure>();
  for (const failure of failures) {
    if (!latestBySubject.has(failure.subject_id)) {
      latestBySubject.set(failure.subject_id, failure);
    }
  }

  return (
    <Stack gap="md">
      {error && (
        <Alert variant="light" color="red">
          {error.message}
        </Alert>
      )}
      {[...latestBySubject.values()].map((failure) => {
        const repeats = repeatCount(failures, failure);
        const pending = failure.outcome === 'pending';
        return (
          <Alert
            key={failure.id}
            variant="light"
            color={pending ? 'blue' : 'red'}
            icon={<IconAlertTriangle size={16} />}
            title={failure.subject_name ?? 'Report-level failure'}
          >
            <Stack gap="xs">
              <Group gap="xs">
                <Badge size="sm" variant="light">
                  {attemptLabel(failure)}
                </Badge>
                {failure.graph_id && (
                  <Badge size="sm" variant="light" color="gray">
                    {failure.graph_id}
                  </Badge>
                )}
                {failure.failing_node && (
                  <Badge size="sm" variant="light" color="grape">
                    {failure.failing_node}
                  </Badge>
                )}
                {failure.run_status && (
                  <Badge
                    size="sm"
                    variant="light"
                    color={RUN_STATUS_TONE[failure.run_status] ?? 'gray'}
                  >
                    {failure.run_status}
                  </Badge>
                )}
                {pending && (
                  <Badge size="sm" color="blue">
                    retry running
                  </Badge>
                )}
              </Group>

              {repeats > 1 && (
                <Text size="sm" fw={500} c="red">
                  This same error has come back {repeats} times — a retry is unlikely to help until
                  something changes.
                </Text>
              )}

              {failure.error_message && (
                <Box style={{ maxHeight: 220, overflow: 'auto' }}>
                  <Code block style={{ whiteSpace: 'pre-wrap', fontSize: 12 }}>
                    {failure.error_message}
                  </Code>
                </Box>
              )}

              <Group gap="xs" justify="space-between">
                <Group gap="xs">
                  {failure.run_id && (
                    <Tooltip label="LangSmith run id — paste into LangSmith to open the run">
                      <Code>{failure.run_id}</Code>
                    </Tooltip>
                  )}
                </Group>
                <Button
                  size="xs"
                  variant="light"
                  leftSection={<IconRefresh size={14} />}
                  loading={isRetrying && retryingSubject === failure.subject_id}
                  disabled={pending || isRetrying}
                  onClick={() => handleRetry(failure)}
                >
                  {pending ? 'Retry running' : 'Retry this subject'}
                </Button>
              </Group>
            </Stack>
          </Alert>
        );
      })}
    </Stack>
  );
}
